import { ComponentProps } from 'react';
import ActiveToggle from '@copilot/common/components/toggles/campaign/active';
import { CampaignType } from '@copilot/data/responses/interface';
import StatusFormatter from '@copilot/common/components/common/statusFormat';
import { SwitchChangeEventHandler } from 'antd/lib/switch';
import isNil from 'lodash/isNil';
import { useFetchV2 } from '@copilot/common/hooks/common';
import {
    CAMPAIGN_NURTURE_TOGGLE_TRACKING_ID,
    CAMPAIGN_PROSPECTING_TOGGLE_TRACKING_ID,
} from '@copilot/common/tracking/userpilotEventConsts';

type CampaignStatusDisplayProps = {
    isEnabled: boolean;
    statusIconSize?: string;
    statusCustomText?: string | null;
    checkedStatusText?: string;
    uncheckedStatusText?: string;
    statusToggleStyle?: React.CSSProperties | undefined;
    alignment?: ComponentProps<typeof StatusFormatter>['alignment']; // TODO: need to update
    onToggleChange: (value: boolean) => Promise<void>;
    onToggleClick?: SwitchChangeEventHandler;
    approvedDisplay?: JSX.Element;
    campaignType: CampaignType;
};

/**
 * Campaign Status Display that shows a toggle for approved campaigns
 *
 * @param {boolean} isEnabled whether the campaign is enabled
 * @param {boolean} isPaused whether the current campaign is paused
 * @param {string} statusIconSize size for the status icon, in pixels
 * @param {string} statusCustomtext custom text for the approval status
 * @param {string} checkedStatusText the text for the toggle's enabled status; 'Active` is used if not provided
 * @param {string} uncheckedStatusText the text for the toggle's disabled status; 'Disabled' is used if not provied
 * @param {CSSProperties} statusToggleStyle for overriding the default toggle style
 * @param {RowJustify} alignment alignment for the approval status text
 * @param {function} onToggleChange callback for approved campaigns when the toggle is changed
 * @param {function} onToggleClick callback for approved campaigns when the toggle is clicked
 * @param {JSX.Element} approvedDisplay alternate element to display for approved campaigns
 */
function CampaignStatusDisplay({
    isEnabled,
    checkedStatusText,
    uncheckedStatusText,
    statusToggleStyle,
    onToggleChange,
    onToggleClick,
    approvedDisplay,
    campaignType,
}: CampaignStatusDisplayProps) {
    const [{ isFetching: isStatusUpdating }, handleToggleChange] = useFetchV2(onToggleChange);

    return isNil(approvedDisplay) ? (
        <ActiveToggle
            checked={isEnabled}
            onChange={(state) => {
                handleToggleChange(state);
            }}
            onClick={onToggleClick}
            loading={isStatusUpdating}
            style={statusToggleStyle}
            checkedChildren={checkedStatusText}
            unCheckedChildren={uncheckedStatusText}
            data-tracking-id={
                campaignType == CampaignType.Prospecting
                    ? CAMPAIGN_PROSPECTING_TOGGLE_TRACKING_ID
                    : CAMPAIGN_NURTURE_TOGGLE_TRACKING_ID
            }
        />
    ) : (
        approvedDisplay
    );
}

export default CampaignStatusDisplay;
